<template>
  <div class="card-header border-bottom py-1">
    <h4 class="card-title">Products and Services</h4>
  </div>
  <div class="card-body ps-body">
    <p class="ps-intro">
      Billed to this contact on every Bill Process run at the Rate, from the Billing Start Date. VAT, Interval and Billing Day are saved for reference. Remove a product to stop billing.
    </p>
    <form @submit.prevent="submit">
      <div
          v-for="(item, index) in products"
          :key="index"
          :id="'ps-card-' + index"
          class="ps-card"
          :class="{'ps-card--error': hasError(index)}"
      >
        <div class="ps-card-head">
          <span class="ps-badge">#{{ index + 1 }}</span>
          <span class="ps-product-name">{{ item.product ? item.product.text : 'New product' }}</span>
          <span class="ps-row-total">
            <span class="ps-row-total-label">Total / period</span>
            {{ formatCurrency2D(getRowTotal(item)) }}
          </span>
          <button
              v-if="products.length > 1"
              @click="remove(index)"
              class="btn btn-sm btn-outline-danger ps-remove"
              type="button"
              title="Remove"
          >
            <span><i class="fa fa-trash"></i></span>
          </button>
        </div>
        <div class="row g-50">
          <div class="col-12 col-md-6 col-xl-4">
            <label class="ps-label">Product / Service <span class="text-danger">*</span></label>
            <AsyncSelect
                placeholder="Select Product"
                v-model="item.product"
                :api-service="fetchProductList"
                :additional-query="productQuery"
                label="text"
                :format-label="option => option.text"
                @update:modelValue="val => item.product_id = val ? val.id : null"
            />
            <span v-if="getError(index, 'product_id')" class="ps-error">{{ getError(index, 'product_id') }}</span>
          </div>
          <div class="col-6 col-md-3 col-xl-2">
            <label class="ps-label">Rate / period <span class="text-danger">*</span></label>
            <input
                v-model="item.rate"
                @input="calculateVatAmount(item)"
                type="number"
                class="form-control"
                :class="{'is-invalid': getError(index, 'rate')}"
                placeholder="0.00"
                min="0"
                step="any"
            />
            <span v-if="getError(index, 'rate')" class="ps-error">{{ getError(index, 'rate') }}</span>
          </div>
          <div class="col-6 col-md-3 col-xl-2">
            <label class="ps-label">VAT</label>
            <div class="input-group">
              <input
                  v-model="item.vat"
                  @input="calculateVatAmount(item)"
                  type="number"
                  class="form-control"
                  :class="{'is-invalid': getError(index, 'vat')}"
                  placeholder="0"
                  min="0"
                  step="any"
              />
              <span class="input-group-text">%</span>
            </div>
            <span v-if="getError(index, 'vat')" class="ps-error">{{ getError(index, 'vat') }}</span>
          </div>
          <div class="col-6 col-md-3 col-xl-2">
            <label class="ps-label" title="Auto-calculated from Rate × VAT %, editable">VAT Amount</label>
            <input
                v-model="item.vat_amount"
                @input="handleVatAmountChange(item)"
                type="number"
                class="form-control"
                placeholder="Auto"
                min="0"
                step="any"
            />
          </div>
          <div class="col-6 col-md-3 col-xl-2">
            <label class="ps-label">Interval <span class="text-danger">*</span></label>
            <select
                v-model="item.billing_interval"
                class="form-select"
                :class="{'is-invalid': getError(index, 'billing_interval')}"
            >
              <option value="" disabled>Select</option>
              <option v-for="interval in billingIntervals" :key="interval.id" :value="interval.id">
                {{ interval.name }}
              </option>
            </select>
            <span v-if="getError(index, 'billing_interval')" class="ps-error">{{ getError(index, 'billing_interval') }}</span>
          </div>
          <div class="col-6 col-md-3 col-xl-2">
            <label class="ps-label">Billing Processing Day</label>
            <input
                v-model="item.billing_day"
                type="number"
                class="form-control"
                :class="{'is-invalid': getError(index, 'billing_day')}"
                placeholder="1–31"
                min="1"
                max="31"
                step="1"
            />
            <span v-if="getError(index, 'billing_day')" class="ps-error">{{ getError(index, 'billing_day') }}</span>
          </div>
          <div class="col-6 col-md-3 col-xl-2">
            <label class="ps-label">Billing Start/Effective Date <span class="text-danger">*</span></label>
            <input
                v-model="item.effective_date"
                type="date"
                class="form-control"
                :class="{'is-invalid': getError(index, 'effective_date')}"
            />
            <span v-if="getError(index, 'effective_date')" class="ps-error">{{ getError(index, 'effective_date') }}</span>
          </div>
          <div class="col-12 col-md-12 col-xl-8">
            <label class="ps-label">Remarks</label>
            <input
                v-model="item.remarks"
                type="text"
                class="form-control"
                placeholder="Internal note, not printed on bill (optional)"
            />
          </div>
        </div>
      </div>
      <div class="ps-add">
        <button @click="addMore" class="btn btn-sm btn-outline-primary" type="button">
          <span><i class="fa fa-plus"></i></span> Add Product / Service
        </button>
      </div>
      <div class="ps-footer">
        <div class="ps-summary">
          <span>{{ products.length }} {{ products.length === 1 ? 'item' : 'items' }}</span>
          <span>Rate <strong>{{ formatCurrency2D(totalRate) }}</strong></span>
          <span>VAT <strong>{{ formatCurrency2D(totalVat) }}</strong></span>
          <span>Total <strong>{{ formatCurrency2D(totalRate + totalVat) }}</strong></span>
        </div>
        <div class="ps-actions">
          <button @click="reset" type="button" class="btn btn-outline-secondary">Reset</button>
          <button type="submit" class="btn btn-primary">Save</button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import {computed, defineProps, nextTick, onMounted, ref, watch} from "vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handlePurchaseAndSales from "@/services/modules/purchase";
import handleInventory from "@/services/modules/inventory";
import {formatCurrency2D} from "@/services/utils/global";
import {useRoute} from "vue-router";

const props = defineProps({
  items: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['storeContact'])

const route = useRoute()
const {fetchProductList} = handlePurchaseAndSales()
const {fetchHome} = handleInventory()

const productQuery = {company_id: route.params.companyId}
const defaultInterval = 'monthly'

const emptyRow = () => ({
  product_id: null,
  product: null,
  rate: '',
  vat: '',
  vat_amount: '',
  billing_interval: defaultInterval,
  billing_day: '',
  effective_date: '',
  remarks: ''
})

const products = ref([emptyRow()])
const errors = ref([])
const billingIntervals = ref([])

const toAmount = (value) => Number(value) || 0

const totalRate = computed(() => products.value.reduce((sum, item) => sum + toAmount(item.rate), 0))

const totalVat = computed(() => products.value.reduce((sum, item) => sum + toAmount(item.vat_amount), 0))

const getRowTotal = (item) => toAmount(item.rate) + toAmount(item.vat_amount)

const getError = (index, field) => errors.value[index] ? errors.value[index][field] : ''

const hasError = (index) => errors.value[index] ? Object.keys(errors.value[index]).length > 0 : false

const toOptions = (map) => Object.keys(map).map(id => ({id, name: map[id]}))

const calculateVatAmount = (item) => {
  if (item.vat === '' || item.vat === null) {
    item.vat_amount = ''
    return
  }
  item.vat_amount = parseFloat((toAmount(item.rate) * toAmount(item.vat) / 100).toFixed(4))
}

const handleVatAmountChange = (item) => {
  if (item.vat_amount === '' || item.vat_amount === null) {
    item.vat = ''
    return
  }
  const rate = toAmount(item.rate)
  if (!rate) return
  item.vat = parseFloat((toAmount(item.vat_amount) / rate * 100).toFixed(4))
}

const addMore = () => {
  products.value.push(emptyRow())
  errors.value.push({})
  nextTick(() => {
    const card = document.getElementById('ps-card-' + (products.value.length - 1))
    if (!card) return
    card.scrollIntoView({behavior: 'smooth', block: 'center'})
  })
}

const remove = (index) => {
  products.value.splice(index, 1)
  errors.value.splice(index, 1)
}

const reset = () => {
  products.value = [emptyRow()]
  errors.value = []
}

const isValidBillingDay = (value) => {
  if (value === '' || value === null) return true
  const day = Number(value)
  return Number.isInteger(day) && day >= 1 && day <= 31
}

const validate = () => {
  let valid = true
  const seenIds = []
  errors.value = products.value.map(item => {
    const rowErrors = {}
    if (!item.product_id) rowErrors.product_id = 'Product is required'
    if (item.product_id && seenIds.includes(item.product_id)) rowErrors.product_id = 'Duplicate product is not allowed'
    if (item.product_id && !seenIds.includes(item.product_id)) seenIds.push(item.product_id)
    if (!item.rate && item.rate !== 0) rowErrors.rate = 'Rate is required'
    if (toAmount(item.vat) < 0) rowErrors.vat = 'VAT cannot be negative'
    if (!item.billing_interval) rowErrors.billing_interval = 'Interval is required'
    if (!isValidBillingDay(item.billing_day)) rowErrors.billing_day = 'Must be 1 to 31'
    if (!item.effective_date) rowErrors.effective_date = 'Start date is required'
    if (Object.keys(rowErrors).length) valid = false
    return rowErrors
  })
  return valid
}

const submit = () => {
  if (!validate()) return
  const items = products.value.map(item => ({
    ...(item.id ? {id: item.id} : {}),
    product_service: item.product_id,
    rate: item.rate,
    product_service_vat: item.vat,
    product_service_vat_amount: item.vat_amount,
    product_service_billing_interval: item.billing_interval,
    product_service_billing_day: item.billing_day,
    effective_date: item.effective_date,
    remarks: item.remarks
  }))
  emit('storeContact', {items})
}

watch(() => props.items, () => {
  formDataSet()
})

const formDataSet = () => {
  if (!props.items.length) return
  products.value = props.items.map(item => ({
    id: item.id,
    product_id: item.product_service ? Number(item.product_service) : null,
    product: item.product_service ? {id: Number(item.product_service), text: item.product_name ?? ''} : null,
    rate: item.rate ?? '',
    vat: item.product_service_vat ?? '',
    vat_amount: item.product_service_vat_amount ?? '',
    billing_interval: item.product_service_billing_interval || defaultInterval,
    billing_day: item.product_service_billing_day ?? '',
    effective_date: item.effective_date ?? '',
    remarks: item.remarks ?? ''
  }))
  errors.value = []
}

const getBillingIntervals = () => {
  fetchHome().then(res => {
    if (!res.data || !res.data.billing_intervals) return
    billingIntervals.value = toOptions(res.data.billing_intervals)
  }).catch(() => {
    billingIntervals.value = []
  })
}

onMounted(() => {
  getBillingIntervals()
})
</script>

<style scoped>
.ps-body {
  padding: 0.75rem 1rem 1rem;
  background: #fbfcfe;
}

.ps-intro {
  font-size: 0.8rem;
  color: #9aa2b1;
  margin-bottom: 0.6rem;
}

.ps-card {
  background: #fff;
  border: 1px solid #eef1f6;
  border-left: 3px solid #5a8dee;
  border-radius: 8px;
  padding: 0.5rem 0.75rem 0.65rem;
  margin-bottom: 0.6rem;
}

.ps-card--error {
  border-left-color: #ea5455;
}

.ps-card-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.4rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px dashed #e6ebf5;
}

.ps-badge {
  flex-shrink: 0;
  background: #eef4ff;
  color: #5a8dee;
  font-weight: 600;
  font-size: 0.75rem;
  border-radius: 6px;
  padding: 0.1rem 0.45rem;
}

.ps-product-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #2f3a4a;
}

.ps-row-total {
  flex-shrink: 0;
  font-weight: 600;
  color: #2f3a4a;
  white-space: nowrap;
}

.ps-row-total-label {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  color: #9aa2b1;
  margin-right: 0.25rem;
}

.ps-remove {
  flex-shrink: 0;
  padding: 0.25rem 0.5rem;
}

.ps-label {
  display: block;
  margin-bottom: 0.2rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7488;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-error {
  display: block;
  font-size: 12px;
  color: #ea5455;
  margin-top: 0.15rem;
}

.ps-card :deep(.form-control),
.ps-card :deep(.form-select) {
  border-radius: 6px;
}

.ps-add {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.6rem;
}

.ps-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  background: #fff;
  border: 1px solid #eef1f6;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}

.ps-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  color: #6b7488;
  font-size: 0.85rem;
}

.ps-summary strong {
  color: #2f3a4a;
}

.ps-actions {
  display: flex;
  gap: 0.5rem;
}

@media (max-width: 575.98px) {
  .ps-body {
    padding: 0.5rem;
  }

  .ps-card {
    padding: 0.5rem 0.6rem 0.6rem;
  }

  .ps-row-total-label {
    display: none;
  }

  .ps-add .btn,
  .ps-actions,
  .ps-actions .btn {
    flex: 1 1 auto;
  }

  .ps-add .btn,
  .ps-actions {
    width: 100%;
  }
}
</style>
